import React, { useContext, useEffect, useState } from "react";
import { useNavigate, createSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

/*******INTERNAL IMPORTS********/
import { DisplaySpinnerContext } from "../../../../context/ContextIndex";
import {
    DataGridTable,
    GridAction,
    ConfirmationPopup,
    notifyToast,
    ToastType,
    FormPage,
} from "../../../../components-old/ComponentsIndex";
import { PricingPortalAPI } from "../../../../apis/api-index";
import { MdDelete, MdCreate } from "react-icons/md";
import AddIcon from "@mui/icons-material/Add";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import { useSelector } from "react-redux";
import { selectSessionConfig } from "../../../../store/store-selector";
import { AppUtility } from "../../../../utils/utility";
import { usePezodeFormData } from '../../../../hooks/usePezodeFormData';
import { savePezode, getPezodeRange, getPezodeProductMap } from "../../api-handler/api-handler";
import { getPezodeRangeDefaultValue } from "../../pezode-range/create/schema";
import { getPezodeProductDefaultValue } from "../../pezode-product/create/schema";

const PezodeList = () => {
    /**************************************************
    //Form handlers & hooks
    **************************************************/
    const { t } = useTranslation();
    const navigate = useNavigate();
    const spinnerCtx = useContext(DisplaySpinnerContext);
    const settingsConfig = useSelector(selectSessionConfig);

    /**************************************************
    //Form state variables
    **************************************************/
    const [pezodeRecordList, setPezodeRecordList] = useState([]);
    const [dataGridPageSize, setDataGridPageSize] = useState(10);
    const [totalPezode, setTotalPezode] = useState(0);
    const [gridPageSizeOptions /*setGridPageSizeOptions*/] = useState([10, 20, 50]);
    const [popupOpen, setPopupOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    const pezodeLookupData = usePezodeFormData(settingsConfig.businessCode, settingsConfig.countryCode);


    //on click of add icon for creating pezode
    const handleCreatePezodeClick = async () => {
        navigate("/pezode");
    };

    //onclick of edit pezode
    const handleEditPezodeClick = async (param) => {
        let _navigationData = param.recordResponse;
        delete _navigationData.CreatedDate;
        delete _navigationData.ModifiedDate;
        delete _navigationData.CountryCode;
        delete _navigationData.BusinessCode;
        navigate(
            {
                pathname: "/pezode",
                search: createSearchParams({
                    pezodeid: param.recordResponse.PezodeId,
                }).toString(),
            },
            { state: _navigationData }
        );
    };

    //onclick of confirm of delete popup
    const handleConfirm = async () => {
        await deletePezodeRecord(selectedRow);
        setPopupOpen(false);
        setSelectedRow(null);
    };

    //on click of cancel of delete popup
    const handleCancel = () => {
        setSelectedRow(null);
        setPopupOpen(false);
    };

    //onClick of delete in list
    const handleDeletePezodeClick = (event) => {
        try {
            setSelectedRow(event);
            setPopupOpen(true);
        } catch (err) {
            alert(err);
        }
    };

    //on page change
    const handlePageSizeChange = (newPageSize) => {
        setDataGridPageSize(newPageSize);
    };

    //show review modal
    const handleShowSelectedPezodeChanges = (event) => {
        let _navigationData = { previousPath: "/pezodelist" };
        navigate(
            {
                pathname: "/reviewdetails",
                search: createSearchParams({
                    reviewFilter: `&locationkey=${event.id}`,
                }).toString(),
            },
            { state: _navigationData }
        );
    };

    /**************************************************
     //Fetch API & utility
     **************************************************/

    const setPezodeList = async (pezodeList) => {
        try {
            let _reqtCategory = {};
            if (pezodeLookupData?.pestList?.length) {
                _reqtCategory = pezodeLookupData.pestList.map((item) => ({
                    value: item.ReqtCategoryID,
                    label: item.ReqtCategoryDesc,
                }));


                let reqtCategoryMap = {};
                _reqtCategory.forEach(({ value, label }) => {
                    reqtCategoryMap[value?.toString()] = label;
                });
                pezodeList.sort((a, b) => new Date(b.ModifiedDate).getTime() - new Date(a.ModifiedDate).getTime());
                let _pezodeList = pezodeList.map((item) => {
                    return {
                        id: item.PezodeId,
                        location: item.Location,
                        zone: item.ZoneName,
                        device: item.deviceShortname,
                        pest: reqtCategoryMap[item.ICABSPestCode] ? reqtCategoryMap[item.ICABSPestCode] : "",
                        infestationLevel: item.Infestation,
                        toxic: item.Toxic,
                        icabsProductCode: item.ICABSProductCode,
                        recordResponse: item,
                    };
                });

                await setPezodeRecordList(_pezodeList);
                setTotalPezode(_pezodeList.length);
                spinnerCtx.setLoadingIndicator(false);
            }
        } catch (err) {
            spinnerCtx.setLoadingIndicator(false);
            alert(err);
        }
    };

    const FetchPezodeList = async () => {
        try {
            let _pezodeLists = await PricingPortalAPI.getListWithPagination(
                settingsConfig.businessCode,
                settingsConfig.countryCode,
                "getPezodeList",
                "pezodein"
            );
            await setPezodeList(_pezodeLists);
        } catch (err) {
            alert(err);
        }
    };

    const formPreviousDataForDelete = async (param, editPezodeId) => {
        try {
            let _previousState = {}
            _previousState.pezodein = param.recordResponse;
            _previousState.Deleted = true;
            delete _previousState.pezodein.CreatedDate;
            delete _previousState.pezodein.ModifiedDate;
            delete _previousState.pezodein.CountryCode;
            delete _previousState.pezodein.BusinessCode;
            delete _previousState.pezodein.Version;
            delete _previousState.pezodein.PezodeId;
            delete _previousState.pezodein.Status;
            _previousState.pezodein.Location = {
                id: _previousState.pezodein.LocationId,
                label: _previousState.pezodein.Location,
            };



            _previousState.pezodein.Zone = {
                id: _previousState.pezodein.ZoneLocationId,
                label: _previousState.pezodein.ZoneName,
            };

            for (const item of pezodeLookupData.pestList) {
                if (item.ReqtCategoryID === parseInt(_previousState.pezodein.PestCode)) {
                    _previousState.pezodein.Pest = {
                        id: item.ReqtCategoryID,
                        label: item.ReqtCategoryDesc,
                    };
                }
            }

            _previousState.pezodein.Device = {
                id: _previousState.pezodein.DeviceId,
                label: _previousState.pezodein.deviceShortname,
            };

            _previousState.pezodein.InputUnit = {
                id: _previousState.pezodein.InputUnit,
                label: _previousState.pezodein.InputUnit,
            };

            for (const item of pezodeLookupData.icabsProductList) {
                if (item.ProductCode === _previousState.pezodein.ICABSProductCode) {
                    _previousState.pezodein.iCabsProductCode = {
                        id: item.ProductCode,
                        label: item.ProductCode + " - " + item.ProductDesc,
                    };
                }
            };

            _previousState.pezodein.iCabsProductDetails = (_previousState.pezodein.ICABSProductDetailCode || "")
                .split(",").map(v => ({ id: v.trim(), label: v.trim() }));

            _previousState.pezodein.infestationLevel = {
                id: _previousState.pezodein.InfestationLevelId,
                label: _previousState.pezodein.Infestation
            };

            let pezodeRangeListresponse = await getPezodeRange(editPezodeId, settingsConfig);

            const formattedResponse = pezodeRangeListresponse.map(item => ({
                ...item,
                OutputUnit: {
                    id: item.OutputUnit,
                    label: item.OutputUnit
                }
            }));

            _previousState.pezoderange = formattedResponse.map((item, index) => ({
                id: index,
                ...getPezodeRangeDefaultValue(item)
            }));

            let productMapResponse = await getPezodeProductMap(editPezodeId, settingsConfig);

            const formattedProductResponse = productMapResponse.map(responseItem => {
                const match = pezodeLookupData.consumableProductList.find(item => item.ConsumableProductId === responseItem.ConsumableProductId);
                let _consumableProduct = {};
                if (match) {
                    _consumableProduct = {
                        id: match.ConsumableProductId,
                        label: match.ShortName
                    };
                }
                return {
                    ...responseItem,
                    ConsumableProduct: _consumableProduct
                }
            });

            _previousState.consumableproductmap = formattedProductResponse.map((item, index) => ({
                id: index,
                ...getPezodeProductDefaultValue(item)
            }));
            return _previousState ? _previousState : null;
        } catch (e) {
            alert(e);
        }
    };

    //Delete API call
    const deletePezodeRecord = async (param) => {
        try {
            spinnerCtx.setLoadingIndicator(true);

            let editPezodeId = param.id;
            let data = await formPreviousDataForDelete(param, editPezodeId);

            const status = await savePezode(data, {
                editPezodeId,
                settingsConfig,
                formdata: data
            });
            if (status?.responseStatus === 200) {
                notifyToast("Sent for Approval", ToastType.SUCCESS);
                await FetchPezodeList();
            } else {
                notifyToast(status.errorMessage ? status.errorMessage : "Pezode deletion failed", ToastType.ERROR);
                spinnerCtx.setLoadingIndicator(false);
            }
        } catch (err) {
            alert(err);
        }
    };

    /**************************************************
    //JSX delcaration
    **************************************************/

    //data grid schema
    const dataGridSchema = [
        {
            field: "location",
            headerName: t("pezodeList.location"),
            flex: 1,
        },
        {
            field: "zone",
            headerName: t("pezodeList.zone"),
            flex: 2,
        },
        {
            field: "device",
            headerName: t("pezodeList.device"),
            flex: 2,
        },
        {
            field: "pest",
            headerName: t("pezodeList.pest"),
            flex: 1,
        },
        {
            field: "infestationLevel",
            headerName: t("pezodeList.infestationLevel"),
            flex: 1,
        },
        {
            field: "toxic",
            headerName: t("pezodeList.toxic"),
            flex: 1,
        },
        {
            field: "icabsProductCode",
            headerName: t("pezodeList.icabsProductCode"),
            flex: 1,
        },
        {
            field: "recordResponse",
            hide: true,
        },
        {
            field: "action",
            headerName: t,
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                return (
                    <GridAction actionsConfig={actionConfigs} row={params.row} />
                );
            },
        },
    ];

    //button configs for add and download list
    const buttonConfigs = [
        {
            buttonText: <AddIcon />,
            buttonType: "filled",
            onClick: handleCreatePezodeClick,
            buttonColor: "#A62B23",
            textColor: "#fff",
            action: "create"
        },
        {
            buttonText: <SystemUpdateAltIcon />,
            buttonType: "outlined",
            onClick: () => {
                AppUtility.handleDownload(pezodeRecordList, "pezodelist.xlsx");
            },
            buttonColor: "#A62B23",
            textColor: "#A62B23",
            action: "download"
        },
    ];

    //action configs for edit and delete record
    const actionConfigs = [
        {
            onClick: handleShowSelectedPezodeChanges,
            action: "review"
        },
        {
            icon: MdCreate,
            onClick: handleEditPezodeClick,
            buttonColor: "#EEEEE",
            action: "edit"
        },
        {
            icon: MdDelete,
            onClick: handleDeletePezodeClick,
            buttonColor: "#A62B23",
            action: "delete"
        },
    ];

    /**************************************************
    //Form on load useEffect
    **************************************************/
    useEffect(() => {
        spinnerCtx.setLoadingIndicator(true);
        FetchPezodeList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pezodeLookupData]);

    return (
        <FormPage isList={true}>
            <DataGridTable
                rows={pezodeRecordList}
                columns={dataGridSchema}
                pageSize={dataGridPageSize}
                pageSizeOptions={gridPageSizeOptions}
                onPageSizeChange={handlePageSizeChange}
                totalRowCount={totalPezode}
                toolbarHeader={t("pezodeList.header")}
                toolbarFilterPlaceholder={t("quoteApproval.toolbarFilterPlaceholder")}
                buttonConfigs={buttonConfigs}
            />
            <ConfirmationPopup
                isOpen={popupOpen}
                onClose={handleCancel}
                onConfirm={handleConfirm}
                confirmationText={t("popup.deletePezodeConfirmationMessage")}
                eventObj={selectedRow}
            />
        </FormPage>
    );
};

export default PezodeList;
